/**
 * Letters & Colors builder
 * Replaces the free-text "Want Multiple Colors?" field with a structured
 * color-group builder: one row per color, each row holding the letters that
 * should be that color. Drives the real cart quantity/price itself (total
 * non-space characters summed across every row, regardless of color) using
 * the same scoped-quantity-input + AIOD-tier-trigger approach as
 * custom-text-pricing.js, but implemented independently here since that file
 * is shared by other products (e.g. the KG sibling) still using the plain
 * free-text field — this only touches the product it's rendered on.
 *
 * Each row's color + letters are pushed as their own line item properties
 * (Color 1 / Letters 1, Color 2 / Letters 2, ...) so they show on the cart
 * line and the order, not just baked into a single summary string.
 */
(function () {
  'use strict';

  function billableChars(text) {
    return (text || '').replace(/\s/g, '').length;
  }

  // Only letters, digits, "!", and spaces are orderable for this product --
  // everything else gets silently stripped as it's typed.
  var ALLOWED_CHARS = /[^A-Za-z0-9! ]/g;
  function sanitizeText(text) {
    return (text || '').replace(ALLOWED_CHARS, '');
  }

  // Each character's glyph image lives on one of 3 small Pages per color
  // (/pages/lcb-glyphs-{color}-{1|2|3}) rather than one page per color with
  // all 37 -- resolving that many file_reference metaobject fields in a
  // single Liquid render reliably 500'd regardless of syntax or page
  // isolation (confirmed: ~14 resolutions is fine, ~37 is not). Only the
  // chunks that actually contain a typed character ever get fetched.
  var GLYPH_GROUPS = ['0123456789ABC', 'DEFGHIJKLMNO', 'PQRSTUVWXYZ!'];
  function glyphGroupForChar(ch) {
    var upper = ch.toUpperCase();
    for (var i = 0; i < GLYPH_GROUPS.length; i++) {
      if (GLYPH_GROUPS[i].indexOf(upper) !== -1) return i + 1;
    }
    return null;
  }

  class LettersColorBuilder {
    constructor(root) {
      this.root = root;
      this.colors = JSON.parse(root.dataset.colors || '[]');
      this.glyphs = {};
      this.glyphChunksRequested = {};
      this.minQtyDefault = parseInt(root.dataset.minQty || '4', 10);
      this.showPreview = root.dataset.showPreview === 'true';

      this.rows = [{ color: this.colors[0] ? this.colors[0].name : '', text: '' }];
      this.rowPropEls = [];

      this.productInfo = root.closest('product-info') || document.querySelector('product-info');
      this.productForm = this._findForm();

      // Multi-font support (custom-yard-letters only -- see
      // snippets/letters-color-builder.liquid). The color list itself never
      // varies by font; only the glyph-fetch page slug does (see
      // _ensureGlyphsLoaded), so switching fonts doesn't touch this.colors.
      this.multiFont = root.dataset.multiFont === 'true';
      this.fontSlugs = JSON.parse(root.dataset.fontSlugs || '{}');
      this.variantFontMap = JSON.parse(root.dataset.variantFontMap || '{}');
      this.currentFont = this._detectCurrentFont();

      this._cacheEls();
      this._injectHiddenInputs();
      this._bindStatic();
      this._bindFontChange();
      this._computeDropdownWidth();
      this._renderRows();
      this.update();
      // .cs-price-block is created asynchronously by unit-price.js -- retry
      // shortly after in case it wasn't there yet on the first pass (same
      // pattern custom-text-pricing.js uses for the same reason).
      requestAnimationFrame(() => this.update());
      window.setTimeout(() => this.update(), 250);
    }

    // Fetches only the glyph chunks actually needed for what's currently
    // typed (see GLYPH_GROUPS above) -- never the whole alphabet, and never
    // for a color no row is using. Safe to call repeatedly; already-fetched
    // or in-flight chunks are skipped via glyphChunksRequested. Runs async
    // and re-renders the preview as each chunk lands; until then (or if a
    // fetch fails) _renderPreview()'s per-character fallback keeps the
    // preview usable with plain colored text.
    _ensureGlyphsLoaded() {
      if (!this.showPreview) return;

      const needed = {}; // "colorSlug-group" -> {colorName, colorSlug, group}
      this.rows.forEach((row) => {
        if (!row.color || !row.text) return;
        // "Black Sparkle" -> "black-sparkle" to match the multi-word color
        // Pages' handles (single-word solid colors are unaffected).
        const colorSlug = row.color.toLowerCase().replace(/\s+/g, '-');
        Array.from(row.text).forEach((ch) => {
          if (!ch.trim()) return;
          const group = glyphGroupForChar(ch);
          if (!group) return;
          const key = colorSlug + '-' + group;
          if (this.glyphChunksRequested[key]) return;
          needed[key] = { colorName: row.color, colorSlug: colorSlug, group: group };
        });
      });

      // Font slug prefix for the Page URL -- '' for Bebas (its ~84 Pages
      // predate multi-font support and have no prefix), the font's slug
      // otherwise. Single-font products (multiFont false) never prefix.
      const fontPrefix = this.multiFont ? this.fontSlugs[this.currentFont] || '' : '';

      Object.keys(needed).forEach((key) => {
        this.glyphChunksRequested[key] = true;
        const chunk = needed[key];
        const pageSlug = (fontPrefix ? fontPrefix + '-' : '') + chunk.colorSlug;
        fetch('/pages/lcb-glyphs-' + pageSlug + '-' + chunk.group)
          .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
          .then((html) => this._extractGlyphJson(html))
          .then((data) => {
            const glyphsForColor = data && data[chunk.colorName];
            if (!glyphsForColor) return;
            this.glyphs[chunk.colorName] = Object.assign({}, this.glyphs[chunk.colorName], glyphsForColor);
            this._renderPreview();
          })
          .catch(() => {
            // Leave it un-marked as loaded-successfully in spirit, but don't
            // retry automatically -- the plain-text fallback covers it, and
            // a color/text change will naturally attempt other chunks.
          });
      });
    }

    // Fetching a Page always returns the full themed HTML (?layout=none
    // isn't honored on this storefront) -- pull the JSON out of the
    // <script type="application/json" id="lcb-glyph-json"> tag
    // sections/lcb-glyph-data.liquid embeds, rather than treating the whole
    // response body as JSON. Uses DOMParser + getElementById rather than a
    // regex: some third-party script on the page embeds a JSON-serialized
    // copy of the page's own HTML elsewhere (an escaped duplicate of our
    // tag's markup), and a regex over the raw text can match that escaped
    // copy instead of the real element. DOMParser only resolves actual DOM
    // elements, so it can't be fooled by text that merely looks like a tag.
    _extractGlyphJson(html) {
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const el = doc.getElementById('lcb-glyph-json');
        if (!el) return null;
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    // ── Setup ─────────────────────────────────────────────────────

    _findForm() {
      if (this.productInfo) {
        return (
          this.productInfo.querySelector('product-form form') ||
          this.productInfo.querySelector('form[action*="/cart/add"]') ||
          null
        );
      }
      return (
        document.querySelector('form[action*="/cart/add"]') ||
        document.querySelector('.product-form form') ||
        null
      );
    }

    _cacheEls() {
      this.rowsEl = this.root.querySelector('[data-lcb-rows]');
      this.addRowBtn = this.root.querySelector('[data-lcb-add-row]');
      this.previewEl = this.root.querySelector('[data-lcb-preview]');
      this.previewWrap = this.root.querySelector('[data-lcb-preview-wrap]');
      this.previewScene = this.root.querySelector('[data-lcb-preview-scene]');
      this.errorEl = this.root.querySelector('[data-lcb-error]');
    }

    _injectHiddenInputs() {
      if (!this.productForm) return;

      this.lettersProp = document.createElement('input');
      this.lettersProp.type = 'hidden';
      this.lettersProp.name = 'properties[Letters]';
      this.productForm.appendChild(this.lettersProp);

      const flag = document.createElement('input');
      flag.type = 'hidden';
      flag.name = 'properties[_lcb]';
      flag.value = '1';
      this.productForm.appendChild(flag);
    }

    _bindStatic() {
      if (this.addRowBtn) {
        this.addRowBtn.addEventListener('click', () => {
          const used = this.rows.map((r) => r.color);
          const next = this.colors.find((c) => used.indexOf(c.name) === -1) || this.colors[0];
          this.rows.push({ color: next ? next.name : '', text: '' });
          this._renderRows();
          this.update();
        });
      }

      document.addEventListener('click', () => this._closeAllDropdowns());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this._closeAllDropdowns();
      });

      if (this.productForm) {
        this.productForm.addEventListener('product-form:validate', (e) => this._validateSubmit(e));
      }
    }

    // Reads the currently-selected variant id off the product form's hidden
    // `id` input -- the same source unit-price.js's badge updates rely on --
    // and maps it to a font display name via lcb_variant_font_map. Falls
    // back to the first font-slug key if the map has no entry (e.g. the
    // page loaded before the id input reflects a variant yet).
    _detectCurrentFont() {
      if (!this.multiFont) return null;
      const idInput = this.productForm && this.productForm.querySelector('input[name="id"]');
      const vid = idInput ? idInput.value : null;
      return (vid && this.variantFontMap[vid]) || Object.keys(this.fontSlugs)[0] || null;
    }

    // Re-checks the selected font on every variant change and, if it
    // changed, invalidates the glyph cache -- the same color name (e.g.
    // "Black") means a different image under a different font, so a glyph
    // fetched under the old font must never be reused after switching.
    //
    // Confirmed in-browser that this theme's variant picker never actually
    // dispatches 'variant:change'/'variant:changed'/'theme:variant:change'
    // (unit-price.js listens for those too, but they're dead code here) --
    // the real, working mechanism unit-price.js relies on for its own
    // variant-driven badge updates is direct input/change listeners on the
    // hidden `id` field plus a MutationObserver on its `value` attribute
    // (some variant-picker implementations set it via setAttribute, which
    // fires neither DOM event). Mirror that exact mechanism here rather than
    // the event names, which do nothing on this product.
    _bindFontChange() {
      if (!this.multiFont) return;
      const idInput = this.productForm && this.productForm.querySelector('input[name="id"]');
      if (!idInput) return;

      const onVariantChange = () => {
        const nextFont = this._detectCurrentFont();
        if (nextFont === this.currentFont) return;
        this.currentFont = nextFont;
        this.glyphs = {};
        this.glyphChunksRequested = {};
        this._renderPreview();
        this._ensureGlyphsLoaded();
      };

      idInput.addEventListener('input', onVariantChange);
      idInput.addEventListener('change', onVariantChange);
      new MutationObserver(onVariantChange).observe(idInput, { attributes: true, attributeFilter: ['value'] });
    }

    // Sizes every color dropdown trigger (and its menu, via the same CSS
    // var) to the natural width of the longest color name in this
    // instance's list -- e.g. sparkle's "Light Blue Sparkle" vs solid's
    // "White" -- instead of a fixed width that either truncates long
    // names or wastes space on short ones. Builds one real
    // .lcb-dd__trigger off-screen (so it picks up the theme's actual
    // font/padding/swatch size) and measures its natural width per name.
    _computeDropdownWidth() {
      const probe = document.createElement('button');
      probe.type = 'button';
      probe.className = 'lcb-dd__trigger';
      probe.style.position = 'absolute';
      probe.style.visibility = 'hidden';
      probe.style.pointerEvents = 'none';
      probe.style.width = 'max-content';
      probe.innerHTML =
        '<span class="lcb-swatch"></span><span class="lcb-dd__label"></span><span class="lcb-dd__chev">▾</span>';
      const label = probe.querySelector('.lcb-dd__label');
      this.root.appendChild(probe);

      let maxWidth = 0;
      this.colors.forEach((c) => {
        label.textContent = c.name;
        maxWidth = Math.max(maxWidth, probe.getBoundingClientRect().width);
      });

      probe.remove();
      if (maxWidth > 0) {
        // +20px covers a vertical scrollbar appearing in the open menu
        // (color lists longer than ~5 push past its max-height) -- without
        // this, the longest name(s) wrap since the scrollbar eats into the
        // width the menu items actually have to render in.
        this.root.style.setProperty('--lcb-dd-width', Math.ceil(maxWidth + 20) + 'px');
      }
    }

    _closeAllDropdowns() {
      this.root.querySelectorAll('.lcb-dd[data-open="true"]').forEach((dd) => {
        dd.setAttribute('data-open', 'false');
        const trigger = dd.querySelector('.lcb-dd__trigger');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    }

    // ── Rows ──────────────────────────────────────────────────────

    hexFor(name) {
      const c = this.colors.find((c) => c.name === name);
      return c ? c.hex : '#000000';
    }

    // Sparkle colors have no flat hex that reads as "sparkle" -- when a
    // color entry carries a real glyph image, swatches use that as a
    // textured background instead of a flat fill.
    _applySwatch(el, name) {
      const c = this.colors.find((c) => c.name === name);
      el.style.backgroundImage = '';
      el.style.background = this.hexFor(name);
      if (c && c.image) {
        el.style.backgroundImage = 'url(' + c.image + ')';
        el.style.backgroundSize = 'cover';
        el.style.backgroundPosition = 'center';
      }
    }

    removeRow(idx) {
      if (this.rows.length <= 1) return;
      this.rows.splice(idx, 1);
      this._renderRows();
      this.update();
    }

    _renderRows() {
      if (!this.rowsEl) return;
      this.rowsEl.innerHTML = '';
      this.rows.forEach((row, idx) => {
        this.rowsEl.appendChild(this._buildRowEl(row, idx));
      });
    }

    _buildRowEl(row, idx) {
      const wrap = document.createElement('div');
      wrap.className = 'lcb-row';

      wrap.appendChild(this._buildDropdown(row, idx));

      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'ctp-input lcb-row__input';
      input.autocomplete = 'off';
      input.spellcheck = false;
      input.value = row.text;
      input.setAttribute('aria-label', 'Letters for color group ' + (idx + 1));
      input.addEventListener('input', () => {
        const before = input.value;
        const filtered = sanitizeText(before);
        if (filtered !== before) {
          // Recompute the cursor position by how much was actually removed
          // ahead of it, so typing an invalid character mid-word doesn't
          // fling the cursor to the end of the field.
          const cursorPos = input.selectionStart || 0;
          const removedBeforeCursor = before.slice(0, cursorPos).length - sanitizeText(before.slice(0, cursorPos)).length;
          input.value = filtered;
          const newPos = Math.max(0, cursorPos - removedBeforeCursor);
          input.setSelectionRange(newPos, newPos);
        }
        row.text = filtered;
        this.update();
      });
      wrap.appendChild(input);

      if (this.rows.length > 1) {
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'lcb-row__remove';
        removeBtn.setAttribute('aria-label', 'Remove color group ' + (idx + 1));
        removeBtn.innerHTML =
          '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M11.06 10l3.713 3.712a.75.75 0 0 1-1.06 1.061L10 11.061l-3.712 3.712a.75.75 0 0 1-1.061-1.06L8.939 10 5.227 6.288a.75.75 0 1 1 1.06-1.061L10 8.939l3.712-3.712a.75.75 0 0 1 1.061 1.06L11.061 10z"/></svg>';
        removeBtn.addEventListener('click', () => this.removeRow(idx));
        wrap.appendChild(removeBtn);
      }

      return wrap;
    }

    _buildDropdown(row, idx) {
      const dd = document.createElement('div');
      dd.className = 'lcb-dd';
      dd.setAttribute('data-open', 'false');

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'lcb-dd__trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-label', 'Color for group ' + (idx + 1));

      const swatch = document.createElement('span');
      swatch.className = 'lcb-swatch';
      this._applySwatch(swatch, row.color);

      const label = document.createElement('span');
      label.className = 'lcb-dd__label';
      label.textContent = row.color;

      const chev = document.createElement('span');
      chev.className = 'lcb-dd__chev';
      chev.setAttribute('aria-hidden', 'true');
      chev.textContent = '▾';

      trigger.appendChild(swatch);
      trigger.appendChild(label);
      trigger.appendChild(chev);
      dd.appendChild(trigger);

      const menu = document.createElement('ul');
      menu.className = 'lcb-dd__menu';
      menu.setAttribute('role', 'listbox');

      this.colors.forEach((c) => {
        const li = document.createElement('li');
        li.className = 'lcb-dd__option';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', row.color === c.name ? 'true' : 'false');

        const dot = document.createElement('span');
        dot.className = 'lcb-swatch';
        this._applySwatch(dot, c.name);
        li.appendChild(dot);
        li.appendChild(document.createTextNode(c.name));

        li.addEventListener('click', () => {
          row.color = c.name;
          this._applySwatch(swatch, c.name);
          label.textContent = c.name;
          menu.querySelectorAll('.lcb-dd__option').forEach((o) => o.setAttribute('aria-selected', 'false'));
          li.setAttribute('aria-selected', 'true');
          this._closeAllDropdowns();
          this.update();
        });

        menu.appendChild(li);
      });

      dd.appendChild(menu);

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = dd.getAttribute('data-open') === 'true';
        this._closeAllDropdowns();
        if (!isOpen) {
          dd.setAttribute('data-open', 'true');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });

      return dd;
    }

    // ── Core update ───────────────────────────────────────────────

    update() {
      let totalChars = 0;
      const byColor = {};
      const colorOrder = [];

      this.rows.forEach((row) => {
        const chars = billableChars(row.text);
        totalChars += chars;
        if (chars > 0) {
          if (byColor[row.color] === undefined) colorOrder.push(row.color);
          byColor[row.color] = (byColor[row.color] || 0) + chars;
        }
      });

      this._setQty(Math.max(1, totalChars));

      if (this.lettersProp) this.lettersProp.value = String(totalChars);
      this._syncRowProperties();

      if (this.productInfo) {
        // Reuses existing theme behavior: unit-price.js reads this to show
        // "$X / letter" instead of "$X / item", and product-info.js reads it
        // to preserve this quantity across a variant swap.
        this.productInfo.setAttribute('data-ctp-active', '');
        this.productInfo.dataset.ctpChars = String(totalChars);
      }

      if (this.showPreview) {
        this._renderPreview();
        this._ensureGlyphsLoaded();
      }
      this._updatePriceBlock(totalChars, byColor, colorOrder);
    }

    // Pushes each row's color + letters as its own line item property
    // (Color 1 / Letters 1, Color 2 / Letters 2, ...), numbered only among
    // rows that actually have text so removing a row never leaves a gap.
    _syncRowProperties() {
      if (!this.productForm) return;

      this.rowPropEls.forEach((el) => el.remove());
      this.rowPropEls = [];

      let n = 0;
      this.rows.forEach((row) => {
        if (row.text.trim() === '') return;
        n += 1;

        const colorInput = document.createElement('input');
        colorInput.type = 'hidden';
        colorInput.name = 'properties[Color ' + n + ']';
        colorInput.value = row.color;
        this.productForm.appendChild(colorInput);
        this.rowPropEls.push(colorInput);

        const lettersInput = document.createElement('input');
        lettersInput.type = 'hidden';
        lettersInput.name = 'properties[Letters ' + n + ']';
        lettersInput.value = row.text.trim();
        this.productForm.appendChild(lettersInput);
        this.rowPropEls.push(lettersInput);
      });
    }

    // Renders each typed character as its real glyph image in the row's
    // chosen color (see snippets/letters-color-builder.liquid for the
    // color+character -> asset URL map). Falls back to plain colored text
    // for spaces and any character with no glyph, so the preview stays
    // usable even if a color/character pair is ever missing an image.
    _renderPreview() {
      if (!this.previewEl) return;
      this.previewEl.innerHTML = '';
      let hasContent = false;

      this.rows.forEach((row) => {
        const colorGlyphs = this.glyphs[row.color] || {};
        Array.from(row.text).forEach((ch) => {
          hasContent = hasContent || ch.trim() !== '';
          const glyphUrl = ch.trim() ? colorGlyphs[ch.toUpperCase()] : null;

          if (glyphUrl) {
            const img = document.createElement('img');
            img.src = glyphUrl;
            img.alt = ch;
            img.className = 'lcb-preview__glyph';
            this.previewEl.appendChild(img);
          } else {
            const span = document.createElement('span');
            span.className = 'lcb-preview__char';
            span.textContent = ch === ' ' ? ' ' : ch;
            span.style.color = this.hexFor(row.color);
            this.previewEl.appendChild(span);
          }
        });
      });

      if (this.previewWrap) this.previewWrap.classList.toggle('lcb-preview--empty', !hasContent);
      this._updateSceneReveal();
    }

    // Grows the visible window of the background photo (and shifts which
    // vertical slice of it shows) as more letters get typed, so the scene
    // starts as a short strip -- mostly grass, a sliver of house peeking in
    // at the top -- and opens up mostly downward (more grass) with a little
    // upward growth too (more house/tree) as the sign gets longer. Both
    // constants below are tuned by eye against the actual background photo;
    // adjust PB_MIN/PB_MAX/SHIFT_MIN/SHIFT_MAX directly if the crop looks
    // off rather than reworking the interpolation.
    _updateSceneReveal() {
      if (!this.previewScene) return;

      const REVEAL_MAX_CHARS = 20; // total chars (all rows) for full reveal
      // PB_MIN is sized to fit one line of the (unscaled-down, same-as-solid-
      // colors) glyph font at its largest clamp value without clipping --
      // see the font-size note on .lcb-preview-scene .lcb-preview in the CSS.
      const PB_MIN = 32; // window height, % of scene width, at 0 chars
      const PB_MAX = 60; // window height, % of scene width, at REVEAL_MAX_CHARS
      const SHIFT_MIN = -34; // translateY %, at 0 chars (mostly-grass slice)
      const SHIFT_MAX = -20; // translateY %, at REVEAL_MAX_CHARS (more house/tree)

      const totalChars = this.rows.reduce((sum, row) => sum + row.text.length, 0);
      const progress = Math.max(0, Math.min(1, totalChars / REVEAL_MAX_CHARS));
      const pb = PB_MIN + (PB_MAX - PB_MIN) * progress;
      const shift = SHIFT_MIN + (SHIFT_MAX - SHIFT_MIN) * progress;

      this.previewScene.style.setProperty('--lcb-scene-pb', pb.toFixed(2) + '%');
      this.previewScene.style.setProperty('--lcb-img-shift', shift.toFixed(2) + '%');
    }

    // unit-price.js builds a .cs-price-qty-row/.cs-price-block card at
    // runtime (it isn't in the server HTML) right before the quantity
    // control — the same card custom-text-pricing.js already patches for
    // Field 1/Field 2 ("Price" -> "N letters", plus a minimum-charge note).
    // Do the same thing here, then insert our color/letters summary right
    // below that label and above the price itself, so shoppers see their
    // actual order composition before the dollar amount.
    _updatePriceBlock(totalChars, byColor, colorOrder) {
      const priceBlock =
        (this.productInfo && this.productInfo.querySelector('.cs-price-block')) ||
        document.querySelector('.cs-price-block');
      if (!priceBlock) return;

      const labelEl = priceBlock.querySelector('.cs-price-label');
      if (labelEl) {
        const displayChars = totalChars > 0 ? totalChars : this._getMinQty();
        labelEl.textContent = displayChars + (displayChars === 1 ? ' total letter' : ' total letters');
      }

      const min = this._getMinQty();
      let noteEl = priceBlock.querySelector('.cs-price-note');
      if (!noteEl) {
        noteEl = document.createElement('div');
        noteEl.className = 'cs-price-note';
        priceBlock.appendChild(noteEl);
      }
      if (totalChars < min) {
        noteEl.textContent = totalChars > 0 ? 'Minimum charge applies: priced as ' + min + ' letters.' : min + ' letter minimum.';
        noteEl.hidden = false;
      } else {
        noteEl.textContent = '';
        noteEl.hidden = true;
      }

      let summaryEl = priceBlock.querySelector('[data-lcb-price-summary]');
      if (!summaryEl) {
        summaryEl = document.createElement('div');
        summaryEl.setAttribute('data-lcb-price-summary', '');
        summaryEl.className = 'lcb-price-summary';
        // Sits right below the "N letters" label and above the price --
        // .cs-price-total is unit-price.js's native price element, already
        // present in this block right after the label.
        const totalEl = priceBlock.querySelector('.cs-price-total');
        if (totalEl) {
          priceBlock.insertBefore(summaryEl, totalEl);
        } else {
          priceBlock.appendChild(summaryEl);
        }
      }
      summaryEl.innerHTML = '';
      colorOrder.forEach((colorName) => {
        const qty = byColor[colorName];
        const item = document.createElement('div');
        item.className = 'lcb-price-summary__item';

        const swatch = document.createElement('span');
        swatch.className = 'lcb-swatch lcb-swatch--sm';
        this._applySwatch(swatch, colorName);

        item.appendChild(swatch);
        item.appendChild(document.createTextNode(colorName + ' · ' + qty + (qty === 1 ? ' letter' : ' letters')));
        summaryEl.appendChild(item);
      });
    }

    // ── Quantity plumbing (mirrors custom-text-pricing.js) ─────────

    _getQtyInput() {
      const sectionId = this.productInfo ? this.productInfo.dataset.section || '' : '';
      const scoped =
        this.productInfo &&
        ((sectionId && document.getElementById('Quantity-' + sectionId)) ||
          this.productInfo.querySelector('.quantity__input') ||
          this.productInfo.querySelector('[name="quantity"]'));

      return scoped || document.querySelector('.quantity__input') || document.querySelector('[name="quantity"]');
    }

    _getMinQty() {
      const qtyInput = this._getQtyInput();
      const attrMin = qtyInput ? parseInt(qtyInput.getAttribute('min') || qtyInput.dataset.min || '0', 10) : 0;
      return Math.max(this.minQtyDefault, attrMin || 0);
    }

    _setQty(n) {
      const qtyInput = this._getQtyInput();
      if (!qtyInput) return;
      const min = this._getMinQty();
      const nextQty = Math.max(min, n);
      qtyInput.value = String(nextQty);
      qtyInput.setAttribute('value', String(nextQty));
      qtyInput.dataset.ctpQuantity = String(nextQty);
      ['input', 'change'].forEach((evt) => qtyInput.dispatchEvent(new Event(evt, { bubbles: true })));
    }

    // ── Validation ────────────────────────────────────────────────

    _validateSubmit(e) {
      const totalChars = this.rows.reduce((sum, r) => sum + billableChars(r.text), 0);
      if (!this.errorEl) return;

      if (totalChars === 0) {
        this.errorEl.textContent = 'Add at least one letter before adding to cart.';
        this.errorEl.classList.add('lcb-error--visible');
        const firstInput = this.root.querySelector('.lcb-row__input');
        if (firstInput) {
          firstInput.focus();
          firstInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        e.detail.blocked = true;
      } else {
        this.errorEl.textContent = '';
        this.errorEl.classList.remove('lcb-error--visible');
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-lcb-root]').forEach((el) => {
      if (el._lcbInit) return;
      el._lcbInit = true;
      new LettersColorBuilder(el);
    });
  }

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
})();
